// The game's bitmap fonts on the page: buzzer_11 for numbers, compass_9 for
// short labels, troll_12 for the words carved in a wood plank and
// virtue_large_bold for the tally counter's digits. Each piece of text is a small canvas drawn in whole device
// pixels, with the words in aria-label for screen readers. The atlases come
// from the countsheeps.com teaser (counting-sheep/teaser/assets/fonts), where
// they were converted from the game's BMFont files.

export type FontName = 'buzzer' | 'compass' | 'troll' | 'virtue' | 'monogram';

// [x, y, width, height, xOffset, yOffset, xAdvance]
type Glyph = [number, number, number, number, number, number, number];

interface FontData {
  lineHeight: number;
  chars: Record<string, Glyph>;
}

export interface Font {
  name: FontName;
  data: FontData;
  image: HTMLImageElement;
  // The rows that hold ink across the whole font, so every line sits the same.
  top: number;
  bottom: number;
  tinted: Map<string, HTMLCanvasElement>;
}

const FILES: Record<FontName, string> = {
  buzzer: 'buzzer_11',
  compass: 'compass_9',
  troll: 'troll_12',
  virtue: 'virtue_large_bold',
  monogram: 'monogram',
};
const SPACE = ' ';
// One font pixel of space between glyphs reads better on a phone than none.
// monogram is a terminal face with its spacing already in each advance.
const TRACKING: Record<FontName, number> = {
  buzzer: 1,
  compass: 1,
  troll: 1,
  virtue: 1,
  monogram: 0,
};

const fonts = new Map<FontName, Promise<Font>>();

const loadImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.addEventListener('load', () => resolve(img));
    img.addEventListener('error', () => reject(new Error(`Could not load ${url}`)));
    img.src = url;
  });

function inkRows(image: HTMLImageElement, data: FontData): { top: number; bottom: number } {
  const canvas = document.createElement('canvas');
  canvas.width = image.width;
  canvas.height = image.height;
  const context = canvas.getContext('2d') as CanvasRenderingContext2D;
  context.drawImage(image, 0, 0);
  const alpha = context.getImageData(0, 0, image.width, image.height).data;
  let top = data.lineHeight;
  let bottom = 0;
  for (const [x, y, width, height, , yOffset] of Object.values(data.chars)) {
    for (let row = 0; row < height; row++) {
      for (let column = 0; column < width; column++) {
        if ((alpha[((y + row) * image.width + x + column) * 4 + 3] ?? 0) === 0) continue;
        top = Math.min(top, row + yOffset);
        bottom = Math.max(bottom, row + yOffset + 1);
      }
    }
  }
  return { top, bottom };
}

export function loadFont(name: FontName): Promise<Font> {
  const kept = fonts.get(name);
  if (kept) return kept;
  const made = Promise.all([
    loadImage(`/fonts/${FILES[name]}.png`),
    fetch(`/fonts/${FILES[name]}.json`).then((response) => response.json() as Promise<FontData>),
  ]).then(([image, data]) => ({ name, data, image, ...inkRows(image, data), tinted: new Map() }));
  fonts.set(name, made);
  return made;
}

// The atlas recoloured once per colour. Every glyph is white in the atlas.
function atlasIn(font: Font, color: string): HTMLCanvasElement {
  const kept = font.tinted.get(color);
  if (kept) return kept;
  const canvas = document.createElement('canvas');
  canvas.width = font.image.width;
  canvas.height = font.image.height;
  const context = canvas.getContext('2d') as CanvasRenderingContext2D;
  context.drawImage(font.image, 0, 0);
  context.globalCompositeOperation = 'source-in';
  context.fillStyle = color;
  context.fillRect(0, 0, canvas.width, canvas.height);
  font.tinted.set(color, canvas);
  return canvas;
}

const glyphOf = (font: Font, character: string): Glyph | undefined =>
  font.data.chars[String(character.codePointAt(0))];

export function widthOf(font: Font, text: string): number {
  let width = 0;
  const tracking = TRACKING[font.name];
  for (const character of text) width += (glyphOf(font, character)?.[6] ?? 0) + tracking;
  return Math.max(0, width - tracking);
}

function paint(
  canvas: HTMLCanvasElement,
  font: Font,
  text: string,
  scale: number,
  color: string,
): void {
  const ratio = window.devicePixelRatio || 1;
  const device = Math.max(1, Math.round(scale * ratio));
  const inkHeight = font.bottom - font.top;
  canvas.width = widthOf(font, text) * device;
  canvas.height = inkHeight * device;
  canvas.style.width = `${canvas.width / ratio}px`;
  canvas.style.height = `${canvas.height / ratio}px`;
  const context = canvas.getContext('2d') as CanvasRenderingContext2D;
  context.imageSmoothingEnabled = false;
  const atlas = atlasIn(font, color);
  let cursor = 0;
  for (const character of text) {
    const glyph = glyphOf(font, character);
    if (!glyph) continue;
    const [x, y, width, height, xOffset, yOffset, advance] = glyph;
    if (character !== SPACE && width > 0 && height > 0) {
      const top = (yOffset - font.top) * device;
      context.drawImage(
        atlas,
        x,
        y,
        width,
        height,
        cursor + xOffset * device,
        top,
        width * device,
        height * device,
      );
    }
    cursor += (advance + TRACKING[font.name]) * device;
  }
}

// Draws `text` into a sprite canvas (`spriteCanvas`), whose transform is
// already one sprite pixel to a font pixel. (x, y) is the top of the ink.
export function drawText(
  context: CanvasRenderingContext2D,
  font: Font,
  text: string,
  x: number,
  y: number,
  color: string,
  fontScale = 1,
): void {
  const atlas = atlasIn(font, color);
  let cursor = x;
  for (const character of text) {
    const glyph = glyphOf(font, character);
    if (!glyph) continue;
    const [gx, gy, width, height, xOffset, yOffset, advance] = glyph;
    if (character !== SPACE && width > 0 && height > 0) {
      context.drawImage(
        atlas,
        gx,
        gy,
        width,
        height,
        cursor + xOffset * fontScale,
        y + (yOffset - font.top) * fontScale,
        width * fontScale,
        height * fontScale,
      );
    }
    cursor += (advance + TRACKING[font.name]) * fontScale;
  }
}

// The height of a line of ink, in font pixels.
export const inkHeight = (font: Font): number => font.bottom - font.top;

// Text in a game font, `scale` CSS pixels to each font pixel. The canvas is
// returned at once and painted when the font has loaded.
export function pixelText(
  text: string,
  font: FontName,
  scale: number,
  color: string,
  className = '',
): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.className = `pixel-text ${className}`.trim();
  canvas.setAttribute('role', 'img');
  canvas.setAttribute('aria-label', text);
  repaint(canvas, text, font, scale, color);
  return canvas;
}

// Draws new text into a canvas made by `pixelText`, for numbers that tick.
export function repaint(
  canvas: HTMLCanvasElement,
  text: string,
  font: FontName,
  scale: number,
  color: string,
): void {
  canvas.setAttribute('aria-label', text);
  canvas.dataset.text = text;
  void loadFont(font).then((loaded) => {
    // A later repaint asked for other text while this font loaded.
    if (canvas.dataset.text === text) paint(canvas, loaded, text, scale, color);
  });
}

// Words in a game font, wrapped to `maxWidth` CSS pixels: one canvas a line,
// stacked in a block. The lines are laid out once the font has loaded.
export function pixelBlock(
  text: string,
  font: FontName,
  scale: number,
  color: string,
  maxWidth: number,
  className = '',
): HTMLElement {
  const block = document.createElement('div');
  block.className = `pixel-block ${className}`.trim();
  block.setAttribute('role', 'img');
  block.setAttribute('aria-label', text);
  void loadFont(font).then((loaded) => {
    const room = Math.max(1, Math.floor(maxWidth / scale));
    const lines: string[] = [];
    let line = '';
    for (const word of text.split(' ')) {
      const next = line ? `${line} ${word}` : word;
      if (line && widthOf(loaded, next) > room) {
        lines.push(line);
        line = word;
      } else {
        line = next;
      }
    }
    if (line) lines.push(line);
    block.replaceChildren(
      ...lines.map((each) => {
        const canvas = document.createElement('canvas');
        canvas.className = 'pixel-text';
        canvas.setAttribute('aria-hidden', 'true');
        paint(canvas, loaded, each, scale, color);
        return canvas;
      }),
    );
  });
  return block;
}

// Starts every font loading, so the first screen draws without a gap.
export function preloadFonts(): void {
  for (const name of Object.keys(FILES) as FontName[]) void loadFont(name);
}
